import React, { useState, useRef, useEffect } from 'react';
import { Plus, MoreVertical, X, SquareDashed } from 'lucide-react';
import { Button } from '@/ui-components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/util-components/dropdown-menu';
import { Dialog, DialogContent, DialogTrigger } from '@/ui-components/ui/dialog';
import { Input } from '@/ui-components/ui/input';
import { Label } from '@/ui-components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui-components/ui/select';
import { Textarea } from '@/ui-components/ui/textarea';
import { cn } from '@/lib/utils/cn';
import { Slot, EntityType } from '../../core/template.schemas';
import { 
  useTemplateSlots,
  useTemplateStore
} from '../hooks/useTemplateStore';
import { useEntityRegistry } from '../hooks/useEntityRegistry';
import { toast } from "@/lib/toast";
import { useTemplatePermissions } from '../hooks/useTemplatePermissions';
import { StandardDialogHeader } from '@/util-components/dialog/components/StandardDialogHeader';
import { DialogFooter } from '@/ui-components/ui/dialog';

interface SlotPanelProps {
  className?: string;
  onClose?: () => void;
  showCloseButton?: boolean;
}



/**
 * Form component for creating/editing slots
 */
interface SlotFormProps {
  slot?: Slot;
  onSubmit: (slot: Omit<Slot, 'id'>) => void;
  onCancel: () => void;
}

export const SlotForm: React.FC<SlotFormProps> = ({ slot, onSubmit, onCancel }) => {
  const { registry } = useEntityRegistry();
  const [name, setName] = useState(slot?.name || '');
  
  // Get available entity types from registry, fallback to 'Player' if registry is empty
  const availableEntityTypes = registry.getTypes();
  const defaultEntityType = availableEntityTypes.includes('Player') ? 'Player' : 
                           (availableEntityTypes.length > 0 ? availableEntityTypes[0] : 'Player');
  
  const [entityType, setEntityType] = useState<EntityType>(slot?.entityType || defaultEntityType as EntityType);
  const [description, setDescription] = useState(slot?.description || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) {
      onSubmit({
        name: name.trim(),
        entityType,
        description: description.trim() || undefined,
      });
    }
  };

  return (
    <form id="slot-form" onSubmit={handleSubmit} className="flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto px-5 sm:px-6 pb-5 sm:pb-6 pt-2 space-y-5 sm:space-y-6">
        <div className="space-y-2">
          <Label htmlFor="slot-name">Name</Label>
          <Input
            id="slot-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z.B. Hauptspieler, Torwart..."
            required
            className="h-11 rounded-xl bg-gray-50/50 border-gray-200 focus-visible:bg-white transition-colors"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="slot-entity-type">Entitätstyp</Label>
          <Select value={entityType} onValueChange={(value) => setEntityType(value as EntityType)}>
            <SelectTrigger className="h-11 rounded-xl bg-gray-50/50 border-gray-200 focus:bg-white transition-colors">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-xl shadow-lg border-gray-100">
              {availableEntityTypes.map((type) => (
                <SelectItem key={type} value={type} className="rounded-lg cursor-pointer">
                  {registry.getEntityDisplayName(type)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="slot-description">Beschreibung (optional)</Label>
          <Textarea
            id="slot-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Kurze Beschreibung des Platzhalters..."
            rows={3}
            className="rounded-xl bg-gray-50/50 border-gray-200 focus-visible:bg-white transition-colors resize-none"
          />
        </div>
      </div>

      <DialogFooter className="px-5 sm:px-6 py-4 shrink-0 border-t border-gray-100 bg-gradient-to-t from-background via-background to-background/95 !flex-col gap-3 sm:!flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="flex w-full sm:w-auto">
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            className="h-11 w-full justify-center rounded-xl px-4 text-sm font-semibold text-gray-600 transition-all hover:bg-gray-100 hover:text-gray-900 sm:w-auto sm:justify-start"
          >
            Abbrechen
          </Button>
        </div>
        <div className="flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
          <Button
            type="submit"
            className="h-11 w-full px-6 sm:px-8 min-w-[140px] rounded-xl font-bold text-sm bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition-all sm:w-auto"
          >
            {slot ? 'Aktualisieren' : 'Erstellen'}
          </Button>
        </div>
      </DialogFooter>
    </form>
  );
};

export const SlotPanel: React.FC<SlotPanelProps> = ({ 
  className, 
  onClose, 
  showCloseButton = false 
}) => {
  const { registry } = useEntityRegistry();
  const { canEditTemplate: canManageSlots } = useTemplatePermissions();

  const slots = useTemplateSlots();

  // Verwende die Store-Funktionen direkt, wie in layers-panel.tsx
  const addSlot = useTemplateStore(s => s.addSlot);
  const updateSlot = useTemplateStore(s => s.updateSlot);
  const deleteSlot = useTemplateStore(s => s.deleteSlot);
  const isSlotInUse = useTemplateStore(s => s.isSlotInUse);
  const getElementsUsingSlot = useTemplateStore(s => s.getElementsUsingSlot);
  
  // State for dialogs and editing
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState<Slot | null>(null);
  const [editingSlotId, setEditingSlotId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when editing starts
  useEffect(() => {
    if (editingSlotId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingSlotId]);

  const handleCreateSlot = (slotData: Omit<Slot, 'id'>) => {
    if (!canManageSlots) return;
    addSlot(slotData);
    setIsCreateDialogOpen(false);
  };

  const handleUpdateSlot = (slotData: Omit<Slot, 'id'>) => {
    if (editingSlot) {
      if (!canManageSlots) {
        return;
      }
      
      if (isSlotInUse(editingSlot.id)) {
        toast.error('Platzhalter wird verwendet und kann nicht bearbeitet werden');
        return;
      }
      
      updateSlot(editingSlot.id, slotData);
      setEditingSlot(null);
    }
  };

  const handleDeleteSlot = (slotId: string) => {
    if (!canManageSlots) return;

    if (isSlotInUse(slotId)) {
      const elementsUsingSlot = getElementsUsingSlot(slotId);
      toast.error(`Dieser Platzhalter wird von ${elementsUsingSlot.length} Element(en) verwendet und kann nicht gelöscht werden.`);
      return;
    }
    deleteSlot(slotId);
  };

  const handleNameDoubleClick = (slot: Slot, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!canManageSlots) {
      return;
    }

    if (isSlotInUse(slot.id)) {
      toast.error('Platzhalter wird verwendet und kann nicht bearbeitet werden');
      return;
    }
    
    setEditingSlotId(slot.id);
    setEditingName(slot.name);
  };

  const handleNameSubmit = () => {
    if (editingSlotId && editingName.trim() !== '') {
      updateSlot(editingSlotId, { name: editingName.trim() });
    }
    setEditingSlotId(null);
    setEditingName('');
  };

  const handleNameCancel = () => {
    setEditingSlotId(null);
    setEditingName('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleNameSubmit();
    } else if (e.key === 'Escape') {
      handleNameCancel();
    }
  };

  return (
    <div className={cn("w-full bg-white h-full flex flex-col", className)}>
      {/* Header nur anzeigen wenn showCloseButton true ist (für Standalone-Nutzung) */}
      {showCloseButton && (
        <div className="px-3 py-3 flex-shrink-0 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SquareDashed className="w-4 h-4 text-gray-500" />
              <h2 className="text-sm font-medium text-gray-700">Platzhalter</h2>
            </div>
            <div className="flex items-center gap-1">
              {/* Create Slot Button */}
              <Dialog open={canManageSlots ? isCreateDialogOpen : false} onOpenChange={(open) => canManageSlots && setIsCreateDialogOpen(open)}>
                <DialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                    disabled={!canManageSlots}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md p-0 pt-[max(0.5rem,env(safe-area-inset-top))] pb-[max(0.5rem,env(safe-area-inset-bottom))] border-0 shadow-2xl left-0 top-0 translate-x-0 translate-y-0 max-w-none max-h-none h-[100vh] w-screen flex flex-col overflow-hidden gap-0 rounded-none sm:left-[50%] sm:top-[50%] sm:h-auto sm:w-full sm:max-h-[85vh] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-3xl sm:pt-0 sm:pb-0">
                  <div className="px-5 sm:px-6 py-4 sm:py-5 shrink-0">
                    <StandardDialogHeader
                      icon={SquareDashed}
                      title="Neuen Platzhalter erstellen"
                      description="Füge einen neuen Platzhalter für dein Template hinzu."
                      className="p-0 border-none"
                    />
                  </div>
                  <SlotForm
                    onSubmit={handleCreateSlot}
                    onCancel={() => setIsCreateDialogOpen(false)}
                  />
                </DialogContent>
              </Dialog>

              {/* Close Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-2 pb-2">
          <div className="space-y-0.5 pt-2">
            {/* Neuen Platzhalter erstellen Button - immer an erster Stelle */}
            {canManageSlots ? (
              <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <DialogTrigger asChild>
                  <div className={cn(
                    "rounded-md transition-all duration-150 cursor-pointer group",
                    "hover:bg-blue-50/60 border border-dashed border-gray-300 hover:border-blue-300",
                    "bg-gray-50/30 hover:bg-blue-50/60"
                  )}>
                    <div className="px-2 py-2">
                      <div className="flex items-center gap-2">
                        {/* Plus Icon */}
                        <div className="text-gray-400 group-hover:text-blue-500 flex-shrink-0 transition-colors">
                          <Plus className="w-3 h-3" />
                        </div>
                        
                        {/* Button Text */}
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-gray-600 group-hover:text-blue-600 transition-colors">
                            Neuen Platzhalter erstellen
                          </div>
                          <div className="text-xs text-gray-400 group-hover:text-blue-400 transition-colors -mt-0.5">
                            Spieler, Team, Sponsor...
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md p-0 pt-[max(0.5rem,env(safe-area-inset-top))] pb-[max(0.5rem,env(safe-area-inset-bottom))] border-0 shadow-2xl left-0 top-0 translate-x-0 translate-y-0 max-w-none max-h-none h-[100vh] w-screen flex flex-col overflow-hidden gap-0 rounded-none sm:left-[50%] sm:top-[50%] sm:h-auto sm:w-full sm:max-h-[85vh] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-3xl sm:pt-0 sm:pb-0">
                  <div className="px-5 sm:px-6 py-4 sm:py-5 shrink-0">
                    <StandardDialogHeader
                      icon={SquareDashed}
                      title="Neuen Platzhalter erstellen"
                      description="Füge einen neuen Platzhalter für dein Template hinzu."
                      className="p-0 border-none"
                    />
                  </div>
                  <SlotForm
                    onSubmit={handleCreateSlot}
                    onCancel={() => setIsCreateDialogOpen(false)}
                  />
                </DialogContent>
              </Dialog>
            ) : (
              <div className={cn(
                "rounded-md transition-all duration-150",
                "border border-dashed border-gray-200 bg-gray-50/30"
              )} aria-disabled>
                <div className="px-2 py-2">
                  <div className="flex items-center gap-2">
                    <div className="text-gray-300 flex-shrink-0">
                      <Plus className="w-3 h-3" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-400">
                        Neuen Platzhalter erstellen
                      </div>
                      <div className="text-xs text-gray-300 -mt-0.5">
                        Nur für Pro Nutzer
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Bestehende Slots */}
            {slots.map((slot) => {
              const inUse = isSlotInUse(slot.id);
              const usageCount = getElementsUsingSlot(slot.id).length;

              return (
                <div
                  key={slot.id}
                  className={cn(
                    "rounded-md transition-all duration-150 cursor-pointer group",
                    "hover:bg-gray-50"
                  )}
                >
                  <div className="px-2 py-1.5">
                    <div className="flex items-center gap-2">
                      {/* Entity Icon - kompakter wie im Layers Panel */}
                      <div className="text-gray-500 flex-shrink-0">
                        {registry.getEntityIcon(slot.entityType, "w-3 h-3")}
                      </div>
                      
                      {/* Slot Info - kompakte einzeilige Darstellung */}
                      <div className="flex-1 min-w-0">
                        {editingSlotId === slot.id ? (
                          <input
                            ref={inputRef}
                            type="text"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            onBlur={handleNameSubmit}
                            onKeyDown={handleKeyDown}
                            className="text-xs font-medium text-gray-800 bg-white border border-blue-300 rounded px-1 py-0.5 w-full focus:outline-none focus:ring-1 focus:ring-blue-500"
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <div 
                            className="text-xs font-medium text-gray-800 truncate cursor-text rounded px-1 py-0.5 -mx-1 -my-0.5"
                            onDoubleClick={(e) => handleNameDoubleClick(slot, e)}
                            title={slot.name}
                          >
                            {slot.name}
                          </div>
                        )}
                        
                        {/* Entity Type als Subtext - nur wenn nicht editiert wird */}
                        {editingSlotId !== slot.id && (
                          <div className="text-xs text-gray-400 truncate -mt-0.5">
                            {registry.getEntityDisplayName(slot.entityType)}
                          </div>
                        )}
                      </div>
                      
                      {/* Usage Indicator - kompakt */}
                      {inUse && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                          <span className="text-xs text-blue-600 font-medium">
                            {usageCount}
                          </span>
                        </div>
                      )}
                      
                      {/* Controls - wie im Layers Panel */}
                      <div className={cn(
                        "flex items-center gap-1 transition-opacity",
                        "opacity-0 group-hover:opacity-100"
                      )}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 flex-shrink-0 hover:bg-gray-200/60"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreVertical className="w-3 h-3 text-gray-400" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem 
                              onClick={() => setEditingSlot(slot)}
                              className="text-xs"
                              disabled={!canManageSlots || inUse}
                            >
                              Bearbeiten {inUse && <span className="ml-1 text-gray-400 font-normal">(verwendet)</span>}
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDeleteSlot(slot.id)}
                              className="text-xs text-red-600"
                              disabled={!canManageSlots || inUse}
                            >
                              Löschen
                              {inUse && <span className="ml-1 text-gray-400 font-normal">(verwendet)</span>}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* Empty State - nur anzeigen wenn keine Slots vorhanden UND der Create-Button nicht angezeigt wird */}
            {slots.length === 0 && (
              <div className="text-center py-6 text-gray-400 text-xs">
                <div className="space-y-2">
                  <SquareDashed className="w-6 h-6 mx-auto text-gray-300" />
                  <div>Erstelle deinen ersten Platzhalter</div>
                  <div className="text-xs text-gray-300">
                    Klicke oben, um zu beginnen
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Slot Dialog */}
      <Dialog open={!!editingSlot} onOpenChange={(open) => !open && setEditingSlot(null)}>
        <DialogContent className="sm:max-w-md p-0 pt-[max(0.5rem,env(safe-area-inset-top))] pb-[max(0.5rem,env(safe-area-inset-bottom))] border-0 shadow-2xl left-0 top-0 translate-x-0 translate-y-0 max-w-none max-h-none h-[100vh] w-screen flex flex-col overflow-hidden gap-0 rounded-none sm:left-[50%] sm:top-[50%] sm:h-auto sm:w-full sm:max-h-[85vh] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-3xl sm:pt-0 sm:pb-0">
          <div className="px-5 sm:px-6 py-4 sm:py-5 shrink-0">
            <StandardDialogHeader
              icon={SquareDashed}
              title="Platzhalter bearbeiten"
              description="Passe die Eigenschaften des Platzhalters an."
              className="p-0 border-none"
            />
          </div>
          {editingSlot && (
            <SlotForm
              slot={editingSlot}
              onSubmit={handleUpdateSlot}
              onCancel={() => setEditingSlot(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
